import { useMemo, useCallback } from 'react';
import type { Analysis, PerformanceSummary, CustomizedBlock } from 'venn-api';
import type { DragStoppedEvent } from '../../data-grid';
import { compact } from 'lodash';
import { AVAILABILITY_KEY, FREQUENCY_KEY, BENCHMARK_KEY, ITALIC_CLASS } from '../customAnalysisContants';
import type { AnalysisRequest, AnalysisResponseParser, PortfolioRowData } from '../types';
import {
  getAvailableRangeValue,
  getMaxFrequencyValue,
  getBenchmarkNameRangeMap,
  handleMetricsReorder,
} from './customBlockUtils';
import { getGroupColDef } from './getGroupColDef';
import type { CellClassParams, ColDef } from 'ag-grid-community';
import { getRangeMapKeyFromRequest, useRequestsRangeMap } from './useRequestsRangeMap';
import { useTheme } from 'styled-components';
import { autoGroupColumnDefStaticConstants, useAgGridStyle } from '../components/grid/AgGridThemeOverrides';
import { useMemoWithArgs } from 'venn-utils';
import { useGetRequestSubjectFee } from '../../blocks/utils/hooks/use-advisory-fee';
import { useLabelColumnWidth } from './useLabelColumnMaxWidth';
import { constSelector, useRecoilValue } from 'recoil';
import {
  blockBenchmarkType,
  flattenedProxyMapState,
  vdparFundIdsState,
  type BlockId,
  isReturnsBasedBlockState,
} from 'venn-state';
import {
  renderSuperscripts,
  usePageProxySymbol,
  usePageVdparSymbol,
} from '../../blocks/components/core/block-subject-footnotes-and-superscripts';

const autoColumnCellClass = ({ data }: CellClassParams) => compact(data.isMetadata ? ITALIC_CLASS : undefined);

const emptyProxyMapConstSelector = constSelector({} as { [fundId: string]: undefined });

const useAnalysisTreeGrid = (
  requests: AnalysisRequest[],
  responseParser: AnalysisResponseParser,
  analyses: (Analysis | undefined)[][],
  blockId: BlockId,
  selectedBlock: CustomizedBlock,
  onReorderMetrics: (newMetricsOrder: string[]) => void,
  inPrintMode?: boolean,
) => {
  const customBenchmarkType = useRecoilValue(blockBenchmarkType(blockId));
  const rangeMap = useRequestsRangeMap(requests);
  const isReturnsBasedBlock = useRecoilValue(isReturnsBasedBlockState(blockId));
  const proxyMap = useRecoilValue(isReturnsBasedBlock ? flattenedProxyMapState : emptyProxyMapConstSelector);
  const proxySymbol = usePageProxySymbol();
  const vdparFundIds = useRecoilValue(vdparFundIdsState);
  const vdparSymbol = usePageVdparSymbol();
  const theme = useTheme();
  const getRequestSubjectFee = useGetRequestSubjectFee();

  const rowData = useMemo(() => {
    const subjectIds = requests.map(getRangeMapKeyFromRequest);
    const rows: PortfolioRowData<PerformanceSummary | string | undefined>[] = inPrintMode
      ? []
      : compact([
          {
            label: AVAILABILITY_KEY,
            key: AVAILABILITY_KEY,
            path: [AVAILABILITY_KEY],
            value: getAvailableRangeValue(rangeMap, subjectIds),
            isStrategy: false,
            allocation: [],
            isMetadata: true,
          },
          {
            label: FREQUENCY_KEY,
            key: FREQUENCY_KEY,
            path: [FREQUENCY_KEY],
            value: getMaxFrequencyValue(rangeMap, subjectIds),
            isStrategy: false,
            allocation: [],
            isMetadata: true,
          },
          customBenchmarkType === 'COMMON' || customBenchmarkType === 'INDIVIDUAL'
            ? {
                label: BENCHMARK_KEY,
                key: BENCHMARK_KEY,
                path: [BENCHMARK_KEY],
                value: requests.map((request) => request.benchmark?.name),
                customBenchmarkType,
                benchmarkNameRangeMap: getBenchmarkNameRangeMap(rangeMap, requests),
                isStrategy: false,
                allocation: [],
                isMetadata: true,
              }
            : undefined,
        ]);
    return [...rows, ...responseParser.parser(selectedBlock, analyses)];
  }, [requests, inPrintMode, rangeMap, customBenchmarkType, responseParser, selectedBlock, analyses]);

  const metricsSettings = responseParser.metricsSettings;

  const gridStyle = useAgGridStyle();
  // TODO(VENN-19715): would like to make getGroupColDef a hook, but it is currently used imperatively by the metricsSettingsColumnParser.
  const columnDefs = useMemoWithArgs(getGroupColDef, [
    requests,
    analyses,
    metricsSettings,
    theme,
    gridStyle,
    selectedBlock?.relativeToBenchmark,
    selectedBlock?.contributionToPercentage,
    selectedBlock?.selectedMetrics,
    selectedBlock?.selectedFactors,
    undefined,
    getRequestSubjectFee,
  ]);

  const onDragStoped = useCallback(
    (e: DragStoppedEvent) => {
      handleMetricsReorder(e, onReorderMetrics, columnDefs);
    },
    [columnDefs, onReorderMetrics],
  );

  const { minWidth: labelColumnMinWidth, maxWidth: labelColumnMaxWidth } = useLabelColumnWidth(columnDefs);

  const autoGroupColumnDef = useMemo(
    (): ColDef => ({
      ...autoGroupColumnDefStaticConstants,
      minWidth: labelColumnMinWidth,
      maxWidth: labelColumnMaxWidth,
      cellClass: autoColumnCellClass,
      cellRenderer: ({ data }: { data: PortfolioRowData<PerformanceSummary | string | undefined> }) =>
        !data.fundId
          ? data.label
          : [
              data.label,
              renderSuperscripts([
                { condition: !!proxyMap?.[data.fundId], symbol: proxySymbol },
                { condition: vdparFundIds.has(data.fundId), symbol: vdparSymbol },
              ]),
            ],
      autoHeight: true,
    }),
    [labelColumnMaxWidth, labelColumnMinWidth, proxyMap, proxySymbol, vdparFundIds, vdparSymbol],
  );
  return {
    rowData,
    columnDefs,
    autoGroupColumnDef,
    onDragStoped,
  };
};

export default useAnalysisTreeGrid;
